'use client'

import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'

import { MenuPulseDot } from './MenuPulseDot'
import { LocaleLink } from '@/components/LocaleLink'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronDown } from 'lucide-react'

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { navigationMenuLinkStyle } from '@/components/ui/navigation-menu'
import { cn } from '@/utilities/ui'

import type { CategoryRailNavItem } from '@/Header/utilities/resolveHeaderNavigation'

const RAIL_MENU_CLOSE_DELAY_MS = 160

/** Hover para abrir; pequeno atraso ao sair para permitir mover o ponteiro até ao menu (portal). */
function useRailMenuHover() {
  const [open, setOpen] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => () => clearTimer(), [clearTimer])

  const openMenu = useCallback(() => {
    clearTimer()
    setOpen(true)
  }, [clearTimer])

  const scheduleClose = useCallback(() => {
    clearTimer()
    timerRef.current = setTimeout(() => setOpen(false), RAIL_MENU_CLOSE_DELAY_MS)
  }, [clearTimer])

  const onOpenChange = useCallback(
    (next: boolean) => {
      clearTimer()
      setOpen(next)
    },
    [clearTimer],
  )

  return {
    open,
    onOpenChange,
    triggerHover: {
      onMouseEnter: openMenu,
      onMouseLeave: scheduleClose,
    } as const,
    contentHover: {
      onMouseEnter: openMenu,
      onMouseLeave: scheduleClose,
    } as const,
  }
}

function isActive(pathname: string, href: string) {
  if (href === '/') return pathname === '/'
  return pathname === href || pathname.startsWith(`${href}/`)
}

type Props = {
  items: CategoryRailNavItem[]
}

function RailSubmenuItem({
  item,
  pathname,
  children,
}: {
  item: CategoryRailNavItem
  pathname: string
  children: NonNullable<CategoryRailNavItem['children']>
}) {
  const t = useTranslations('Header')
  const hover = useRailMenuHover()
  const childActive = children.some((c) => isActive(pathname, c.href))
  const selfActive = isActive(pathname, item.href)
  const railActive = selfActive || childActive

  return (
    <div className="shrink-0 snap-start">
      <DropdownMenu modal={false} open={hover.open} onOpenChange={hover.onOpenChange}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            {...hover.triggerHover}
            className={cn(
              navigationMenuLinkStyle(),
              'group gap-1.5 data-[state=open]:text-foreground',
              railActive && 'text-foreground',
            )}
            data-active={railActive ? '' : undefined}
            aria-label={t('submenuAria', { label: item.label })}
            aria-expanded={hover.open}
          >
            {item.label}
            {item.pulseDot ? <MenuPulseDot /> : null}
            <ChevronDown
              className="relative top-px ml-1 size-3 opacity-70 transition duration-300 group-data-[state=open]/navigation-menu-trigger:rotate-180"
              aria-hidden
            />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          sideOffset={6}
          className="min-w-[10rem]"
          {...hover.contentHover}
        >
          <DropdownMenuItem asChild>
            <LocaleLink
              href={item.href}
              {...(selfActive && !childActive ? { 'aria-current': 'page' as const } : {})}
            >
              {item.label}
            </LocaleLink>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {children.map((sub) => (
            <DropdownMenuItem key={`${sub.href}-${sub.label}`} asChild>
              <LocaleLink
                href={sub.href}
                {...(isActive(pathname, sub.href) ? { 'aria-current': 'page' as const } : {})}
              >
                {sub.label}
              </LocaleLink>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

export function HeaderCategoryRail({ items }: Props) {
  const t = useTranslations('Header')
  const pathname = usePathname()

  return (
    <div className="mx-auto max-w-[var(--site-content-width,1350px)] min-w-0 overflow-visible px-4 lg:px-6">
      <div className="relative w-full min-w-0 overflow-visible">
        <div
          className="pointer-events-none absolute bottom-0 left-0 top-0 z-[2] w-8 bg-gradient-to-r from-background to-transparent md:w-16"
          aria-hidden
        />
        <nav className="relative z-10" aria-label={t('categoryNav')}>
          <div className="no-scrollbar flex h-9 w-full min-w-0 snap-x snap-mandatory flex-nowrap items-center justify-start gap-0 overflow-x-auto scroll-px-3 pl-0">
            {items.map((item) => {
              const children = item.children
              const hasChildren = Array.isArray(children) && children.length > 0

              if (hasChildren) {
                return (
                  <RailSubmenuItem
                    key={`${item.href}-${item.label}`}
                    item={item}
                    pathname={pathname}
                  >
                    {children!}
                  </RailSubmenuItem>
                )
              }

              const active = isActive(pathname, item.href)
              return (
                <div key={`${item.href}-${item.label}`} className="shrink-0 snap-start">
                  <LocaleLink
                    href={item.href}
                    className={navigationMenuLinkStyle()}
                    {...(active ? { 'aria-current': 'page' as const } : {})}
                    data-active={active ? '' : undefined}
                  >
                    {item.label}
                    {/* Ponto opcional, ligado por item no CMS. Ausente = markup de antes. */}
                    {item.pulseDot ? <MenuPulseDot /> : null}
                  </LocaleLink>
                </div>
              )
            })}
          </div>
        </nav>
        <div
          className="pointer-events-none absolute bottom-0 right-0 top-0 z-[2] w-8 bg-gradient-to-l from-background to-transparent opacity-0 md:w-16"
          aria-hidden
        />
      </div>
    </div>
  )
}
